import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { Typography } from '../Typography';

export type BannerHeaderState = 'info' | 'timer';

export interface BannerHeaderProps {
  state?: BannerHeaderState;
  text: string;
  showIcon?: boolean;
  onClick?: () => void;
}

/**
 * Full-width banner for informational and time-sensitive status messages.
 * @see {@link https://www.figma.com/design/jkuMhkzDQH2lAUYa8CQvto/Primer-DS?node-id=8427-1771&m=dev|Figma Design}
 * @see src/primer/README.md
 */
export function BannerHeader({
  state = 'info',
  text,
  showIcon = true,
  onClick,
}: BannerHeaderProps) {
  const isTimer = state === 'timer';
  const Root = onClick ? 'button' : 'div';

  return (
    <Root
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'w-full flex items-center gap-2 px-5 py-3 text-center',
        // Focus ring
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-(--border-color-interactiveloud)',
        isTimer ? 'bg-media' : 'bg-informative',
        onClick && 'cursor-pointer'
      )}
    >
      <span className="flex-1 min-w-0">
        <Typography variant="header" size="h3" color={isTimer ? 'white' : 'body'} as="span">
          {text}
        </Typography>
      </span>
      {showIcon && (
        <Icon
          name={isTimer ? 'circle-info' : 'circle-info-filled'}
          size="large"
          color={isTimer ? 'white' : 'interactive'}
        />
      )}
    </Root>
  );
}
